import { computed } from 'vue'
import { useSalesStore } from '@/stores/sales'
import { useProductSettings } from '@/use/productSettings'

export function useSalesEligibility() {
  const salesStore = useSalesStore()
  const { getProductSettings } = useProductSettings()

  const products = computed(() => salesStore.getProducts || [])
  const bundles = computed(() => salesStore.getBundles || [])
  const bundlesCount = computed(() => bundles.value.length)
  const eligibleActions = computed(() => salesStore.getEligibleActions)

  const bundleProducts = computed(() => {
    const final = []
    salesStore.getBundles?.forEach(b => {
      for (const p of b.products) {
        if (!final.includes(p.product_category)) final.push(p.product_category)
      }
    })
    return final
  })
  const productBundles = computed(() => {
    const final = {}
    salesStore.getBundles?.forEach(b => {
      for (const p of b.products) {
        if (!final[p.product_category]) final[p.product_category] = []
        final[p.product_category].push(b)
      }
    })
    return final
  })

  const productInCart = computed(() => salesStore.getCartList.some(item => item.transaction_type != 'Bundle'))
  const bundleInCart = computed(() => salesStore.getCartList.some(item => item.transaction_type == 'Bundle' || item.promo_code))

  const eligibleBundles = computed(() =>
    (salesStore.getBundles || []).filter(b => salesStore.getEligibleBundleNames.includes(b.bundle_name))
  )

  // Derived in the same key-based format the /sales/recommended_offer endpoint expects
  const eligibleRecommendedOffers = computed(() => {
    const final = {}
    for (const action of eligibleActions.value) {
      const short = getProductSettings(action.product_category).abbreviation
      if (!short) continue
      if (action.action === 'purchase') {
        final[`${short}_PURCH_ELIG`] = true
      } else if (action.action === 'renew' && action.stop_subscription_id) {
        const [orderId, productId] = action.stop_subscription_id.split('.')
        final[`${short}_RENEW_ELIG_${orderId}_${productId}`] = true
      }
    }
    return final
  })

  const allowSale = computed(() => salesStore.getAllowSale)
  const noSaleReason = computed(() => salesStore.getNoSaleReason)

  const isSaleEligible = sub => {
    const subId = `${sub.order_id}.${sub.product_id}`
    return eligibleActions.value.some(a =>
      (a.action === 'purchase' && a.product_category === sub.product_category) ||
      a.stop_subscription_id === subId
    )
  }

  const hasRenewal = product_category =>
    eligibleActions.value.some(a => a.action === 'renew' && a.product_category === product_category)

  const hasRenewalUpgrade = product_category =>
    eligibleActions.value.some(a => a.action === 'upgrade_renew' && a.product_category === product_category)

  const hasProductUpgrade = product_category =>
    eligibleActions.value.some(a => a.action === 'upgrade' && a.product_category === product_category)

  const renewalSub = sub => {
    const subId = `${sub.order_id}.${sub.product_id}`
    return eligibleActions.value.some(a => a.action === 'renew' && a.stop_subscription_id === subId)
  }

  const getTransactionOptions = sub => {
    if (salesStore.getPromoCode) return []
    const final = []
    const subId = `${sub.order_id}.${sub.product_id}`
    const actions = eligibleActions.value

    if (!bundleInCart.value || sub.transaction_type === 'Bundle') {
      // Purchase
      if (actions.some(a => a.action === 'purchase' && a.product_category === sub.product_category) &&
          ['Former', 'Never', 'Declined'].includes(sub.status)) {
        const duplicate = Object.values(salesStore.getCart).some(item => item.product_category === sub.product_category)
        if (!duplicate) {
          final.push('Purchase')
          if (sub.transaction_type === 'Bundle' || (!productInCart.value && bundleProducts.value?.includes(sub.product_category))) {
            final.push('Bundle')
          }
        }
      }
      // Renew
      if (actions.some(a => a.action === 'renew' && a.stop_subscription_id === subId)) {
        final.push('Renew')
        if (!final.includes('Bundle') && bundleProducts.value?.includes(sub.product_category)) final.push('Bundle')
      }
      // Upgrade+Renew (server already excludes HP partner)
      if (actions.some(a => a.action === 'upgrade_renew' && a.stop_subscription_id === subId)) {
        final.push('Upgrade+Renew')
        if (!final.includes('Bundle') && bundleProducts.value?.includes(sub.product_category)) final.push('Bundle')
      }
    }
    return final
  }

  const getProductUpgradeOptions = sub => {
    const final = []
    if (!sub.transaction_type.includes('Upgrade')) return final
    // Tech Support subs only have an Upgrade+Renew catalog entry under LiveTech's category (server-side OVERLAPPING_PRODUCTS reach-around) -- resolve the same way getSizeOptions/getTermOptions already do.
    const options = upgradeProducts.value.filter(p => p.product_category == getSalesProductCategory(sub))
    if (options.length == 0) return final
    if (sub.transaction_type?.includes('Upgrade') || (!sub.transaction_type && getTransactionOptions(sub).includes('Upgrade'))) {
      const sizes = sub.product_size ? [sub.product_size] : getSizeOptions(sub)
      const terms = sub.initial_term ? [sub.initial_term] : getTermOptions(sub)
      options.forEach(p => {
        if (sizes.includes(p.product_size) && terms.includes(p.initial_term) && !final.includes(p.offer_product)) final.push(p.offer_product)
      })
    }
    return final
  }

  const getSalesProductCategory = sub => sub.transaction_type?.includes('Renew') && sub.product_line_category == 'Tech Support' && sub.product_category != 'LiveTech' ? 'LiveTech' : sub.product_category
  const getSalesBillingModel = sub => {
    if (sub.billing_model == 'Yearly') return '1 Year'
    if (sub.billing_model == '1 Month') return 'Monthly'
    return sub.billing_model
  }
  const getTermBillingModel = billing_model => {
    if (billing_model == 'Monthly') return '1 Month'
    if (billing_model == 'Yearly') return '1 Year'
    if (billing_model == 'Once') return 'a One Time use'
    return billing_model
  }

  const getTermOptions = sub => {
    const terms = []
    if (!sub.transaction_type && !sub.product_size) return terms
    let pc = getSalesProductCategory(sub)
    const bt = getSalesBillingModel(sub)
    if (sub.transaction_type == 'Bundle') {
      productBundles.value[pc].forEach(b => {
        const pCount = b.products.length
        for (let i = 0; i < pCount; i++) {
          const p = b.products[i]
          if (p.offer_product == pc
              && (!sub.product_size || p.product_size == sub.product_size)
              && !terms.includes(p.initial_term)
          ) terms.push(p.initial_term)
        }
      })
      return terms
    }
    products.value.forEach(o => {
      if (o.transaction_type == sub.transaction_type
          && o.product_category == pc
          && (!sub.product_size || sub.transaction_type == 'Upgrade+Renew' || o.product_size == sub.product_size)
          && (sub.transaction_type != 'Renew' || o.initial_term == bt)
          && !terms.includes(o.initial_term)) {
        terms.push(o.initial_term)
      }
    })
    return terms
  }

  const isValidSize = (o, sub) => {
    if (sub.product_line == 'IDProtect' && sub.product_size == 'Family' && o.product_size == 'Individual') return false
    return true
  }

  const getSizeOptions = sub => {
    const sizes = []
    if (!sub.transaction_type) return sizes
    let pc = getSalesProductCategory(sub)
    const bt = getSalesBillingModel(sub)
    if (sub.transaction_type == 'Bundle') {
      productBundles.value[pc].forEach(b => {
        const pCount = b.products.length
        for (let i = 0; i < pCount; i++) {
          const p = b.products[i]
          if (p.offer_product == pc
              && (!sub.initial_term || p.initial_term == bt)
              && !sizes.includes(p.product_size)
          ) sizes.push(p.product_size)
        }
      })
      return sizes
    }
    products.value.forEach(o => {
      if (o.transaction_type == sub.transaction_type && o.product_category == pc
          && (sub.transaction_type !== 'Renew' || o.initial_term == bt)
          && (sub.transaction_type !== 'Renew' || sub.transaction_type == 'Upgrade+Renew' || o.product_size == sub.sku_size)
          && !sizes.includes(o.product_size)
          && isValidSize(o, sub)) {
        sizes.push(o.product_size)
      }
    })
    return sizes
  }

  const getBundleProductOptions = sub => {
    const final = []
    if (sub.transaction_type != 'Bundle') return final
    const bunds = productBundles.value[sub.product_category]
    if (bunds) {
      bunds.forEach(b => {
        const prod = b.products.find(p => p.product_category == sub.product_category)
        if (sub.initial_term == prod.initial_term && sub.product_size == prod.product_size) {
          const pCount = b.products.length
          for (let i = 0; i < pCount; i++) {
            const p = b.products[i]
            if (p.product_category != sub.product_category && !final.includes(p.product_category)) {
              final.push(p.product_category)
            }
          }
        }
      })
    }
    return final
  }

  const getBundleSizeOptions = sub => {
    const final = []
    if (sub.transaction_type != 'Bundle' || !sub.bundle_product) return final
    let pc = getSalesProductCategory(sub)
    const bCount = productBundles.value[pc].length
    for (let a = 0; a < bCount; a++) {
      const b = productBundles.value[pc][a]
      if (b.products.some(p => p.offer_product == pc && p.initial_term == sub.initial_term && p.product_size == sub.product_size)) {
        const pCount = b.products.length
        for (let i = 0; i < pCount; i++) {
          const p = b.products[i]
          if (p.offer_product != pc && sub.bundle_product == p.product_category && (!sub.bundle_term || sub.bundle_term == p.initial_term) && !final.includes(p.product_size)) final.push(p.product_size)
        }
      }
    }
    return final
  }

  const getBundleTermOptions = sub => {
    const final = []
    if (sub.transaction_type != 'Bundle' || !sub.bundle_product || !sub.bundle_size) return final
    let pc = getSalesProductCategory(sub)
    const bCount = productBundles.value[pc].length
    for (let a = 0; a < bCount; a++) {
      const b = productBundles.value[pc][a]
      if (b.products.some(p => p.offer_product == pc && p.initial_term == sub.initial_term && p.product_size == sub.product_size)) {
        const pCount = b.products.length
        for (let i = 0; i < pCount; i++) {
          const p = b.products[i]
          if (p.offer_product != pc && sub.bundle_product == p.product_category && sub.bundle_size == p.product_size && !final.includes(p.initial_term)) final.push(p.initial_term)
        }
      }
    }
    return final
  }

  const getPromoOptions = computed(() => {
    const codes = []
    const cartCount = salesStore.getCartCount
    if (!cartCount) return codes
    if (cartCount > 1) {
      const cartIds = salesStore.getCartList.map(p => p.offer_configuration_id)
      for (let b = 0; b < bundlesCount.value; b++) {
        const bundle = bundles.value[b]
        const prodCount = bundle.products.length
        if (prodCount != cartCount) continue
        const ids = [...cartIds]
        let index = null
        for (let i = 0; i < prodCount; i++) {
          index = ids.indexOf(bundle.products[i].offer_configuration_id)
          if (index >= 0) ids.splice(index, 1)
          else break
        }
        if (index >= 0 && !ids.length) codes.push(bundle.promo_code)
      }
    } else {
      for (let i = 0; i < cartCount; i++) {
        let c = salesStore.getCartList[i]
        products.value.forEach(product => {
          if (product.offer_configuration_id == c.offer_configuration_id
            && product.transaction_type == c.transaction_type
            && !codes.includes(product.promo_code)
          ) {
            codes.push(product.promo_code)
          }
        })
      }
    }
    if (codes.length == 1 && codes[0] == null) return []
    return codes
  })

  const saleProducts = computed(() => {
    const final = []
    salesStore.getProducts?.forEach(p => p.transaction_type == 'Purchase' && !final.includes(p) ? final.push(p) : null)
    return final
  })
  const renewalProducts = computed(() => {
    const final = []
    salesStore.getProducts?.forEach(p => p.transaction_type.includes('Renew') && !final.includes(p) ? final.push(p) : null)
    return final
  })
  const upgradeProducts = computed(() => {
    const final = []
    salesStore.getProducts?.forEach(p => p.transaction_type.includes('Upgrade') && !final.includes(p) ? final.push(p) : null)
    return final
  })
  const straightUpgradeProducts = computed(() => {
    const final = []
    upgradeProducts.value.forEach(p => p.transaction_type == 'Upgrade' && !final.includes(p) ? final.push(p) : null)
    return final
  })
  const renewalUpgradeProducts = computed(() => {
    const final = []
    renewalProducts.value.forEach(p => p.transaction_type == 'Upgrade+Renew' && !final.includes(p) ? final.push(p) : null)
    return final
  })

  return {
    eligibleBundles, eligibleRecommendedOffers, isSaleEligible, saleProducts, renewalProducts, renewalUpgradeProducts, straightUpgradeProducts,
    bundleProducts, productBundles, getBundleProductOptions, getBundleTermOptions, getBundleSizeOptions, getSalesProductCategory,
    getTransactionOptions, getTermOptions, getSizeOptions, getPromoOptions, getProductUpgradeOptions, hasRenewal, hasRenewalUpgrade, hasProductUpgrade,
    bundleInCart, renewalSub, getTermBillingModel, allowSale, noSaleReason
  }
}
